import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import { useTheme } from '@mui/styles';

import LoadingButton from '@mui/lab/LoadingButton';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import messages from './messages';
import VerificationDeclineForm from '../VerificationDeclineForm/VerificationDeclineForm';

import {
  ASSET_TYPE,
  useGetLanguageScores,
} from 'src/pages/VerificationsPage/api';

import ConfirmationDialog from 'src/components/ConfirmationDialog';
import AutoCompleteField from 'src/components/AutoCompleteField';

interface Props {
  file?: UCM.VerificationLanugagesType | null;
  handleApprove: (params: Record<string, object>) => void;
  handleReject: (params: Record<string, string | object>) => void;
  loading: boolean;
}

interface RatingScore {
  code: string;
  name: string;
  score: string | number | null;
}

export default function VerificationLanguageFields({
  file,
  handleApprove,
  handleReject,
  loading,
}: Props) {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  const [ratingScores, setRatingScores] = useState<Record<string, RatingScore>>(
    {},
  );

  const { data: languageScores } = useGetLanguageScores();

  useEffect(() => {
    if (file?.ratings) {
      const transformedRatings = keyBy(file?.ratings, 'code') as Record<
        string,
        RatingScore
      >;
      setRatingScores(transformedRatings);
    } else {
      setRatingScores({});
    }
  }, [file]);

  const handlePressApprove = useCallback(async () => {
    const ratings = map(ratingScores, (rating) => ({
      criterion_code: rating.code,
      score: rating.score ? Number(rating.score) : null,
    }));
    handleApprove({ ratings });
  }, [ratingScores, handleApprove]);

  const handleDecline = useCallback(
    (reasonCode: string) => {
      const ratings = map(ratingScores, (rating) => ({
        criterion_code: rating.code,
        score: rating.score ? Number(rating.score) : null,
      }));
      handleReject({
        ratings,
        reason_code: reasonCode,
      });
    },
    [ratingScores, handleReject],
  );

  const handleChangeRatingScore = useCallback(
    (
      newValue:
        | { label: string; value: string }
        | { label: string; value: string }[]
        | null,
      code: string,
    ) => {
      if (!newValue || Array.isArray(newValue)) return;
      setRatingScores((prevRatingScores) => ({
        ...prevRatingScores,
        [code]: { ...prevRatingScores[code], score: newValue.value },
      }));
    },
    [],
  );

  const disabledButtons = useMemo(() => {
    return !file || loading;
  }, [file, loading]);

  return (
    <Stack
      borderRadius={1.5}
      bgcolor={theme.palette.common.white}
      p={1.5}
      direction="column"
      spacing={1}
    >
      <Typography variant="inherit" fontSize={12}>
        {file?.fileName}
      </Typography>
      <Typography
        variant="inherit"
        fontSize={12}
        color={theme.palette.grey[700]}
      >
        {formatMessage(messages.uploadedOnLabel, {
          date: file?.uploadedAt ?? '-',
        })}
      </Typography>

      <Grid container spacing={2}>
        {ratingScores &&
          map(ratingScores, (rating, code) => (
            <Grid key={code} size={{ xs: 12, md: 4 }}>
              <AutoCompleteField
                label={rating.name}
                options={languageScores ?? []}
                value={rating.score?.toString() || ''}
                onChange={(value) => handleChangeRatingScore(value, code)}
              />
            </Grid>
          ))}
      </Grid>
      <Grid container spacing={1} justifyContent="flex-end" mt={1}>
        <VerificationDeclineForm
          assetType={ASSET_TYPE.USER_LANGUAGE}
          onConfirm={handleDecline}
          disabled={disabledButtons}
          loading={loading}
        />
        <ConfirmationDialog
          onConfirm={handlePressApprove}
          message={formatMessage(messages.approveConfirmMessage)}
        >
          {(handleOpen) => (
            <LoadingButton
              variant="contained"
              color="primary"
              disabled={disabledButtons}
              onClick={handleOpen}
              loading={loading}
            >
              {formatMessage(messages.approveButton)}
            </LoadingButton>
          )}
        </ConfirmationDialog>
      </Grid>
    </Stack>
  );
}
